import { useAbsenceTypes, useTimeline } from '@src/features/absence';
import { useAuthentication } from '@src/features/auth';
import { useFormContext } from '@src/shared/form';
import { DateField } from '@src/shared/ui/fields';
import { Stack } from '@src/shared/ui/layout';
import { type ComponentPropsWithoutRef, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { canBackdateAbsence } from '../../../lib';
import type { ScheduleFormValues } from '../Form';

type DateFieldProps = ComponentPropsWithoutRef<typeof DateField>;

function FromField(props: DateFieldProps) {
  const { t } = useTranslation();

  return (
    <DateField
      fullWidth
      label={t('absence.schedule.field_date_from', 'From')}
      name="dateFrom"
      showCalendarIcon={false}
      {...props}
    />
  );
}

function ToField(props: DateFieldProps) {
  const { t } = useTranslation();
  return (
    <DateField
      fullWidth
      label={t('absence.schedule.field_date_to', 'To')}
      name="dateTo"
      showCalendarIcon={false}
      {...props}
    />
  );
}

export type DatesChangedPayload = {
  dateFrom?: Date | null;
  dateTo?: Date | null;
};

type DatesRangeFieldProps = {
  hours: boolean;
  disabled?: boolean;
  /** Hard bounds (withdrawal): the range may only be narrowed within these. */
  minDate?: Date;
  maxDate?: Date;
};
export function DatesRangeField({
  hours,
  disabled = false,
  minDate,
  maxDate,
}: DatesRangeFieldProps) {
  const { watch } = useFormContext() as UseFormReturn<ScheduleFormValues>;
  const { user } = useAuthentication();
  const { users } = useTimeline();
  const { absenceTypes } = useAbsenceTypes();
  const dateFrom = watch('dateFrom');
  const dateTo = watch('dateTo');
  const userId = watch('userId');
  const typeId = watch('typeId');

  const canSelectPastDates = canBackdateAbsence(
    typeId,
    absenceTypes,
    users.find((u) => u.id === userId),
    user,
  );

  const today = useMemo(() => new Date(), []);
  const permissionMinFrom = canSelectPastDates ? undefined : today;
  const permissionMinTo = canSelectPastDates
    ? (dateFrom ?? undefined)
    : dateFrom && dateFrom > today
      ? dateFrom
      : today;

  // Explicit bounds win over the permission floor, so a withdrawal can still
  // reach the first day of a past absence.
  const latest = (...dates: (Date | undefined)[]) =>
    dates
      .filter((date): date is Date => !!date)
      .reduce((left, right) => (right > left ? right : left), new Date(0));
  const earliest = (...dates: (Date | undefined)[]) => {
    const present = dates.filter((date): date is Date => !!date);
    return present.length
      ? present.reduce((left, right) => (right < left ? right : left))
      : undefined;
  };

  const minDateFrom = minDate ?? permissionMinFrom;
  const maxDateFrom = earliest(dateTo ?? undefined, maxDate);
  const minDateTo = minDate
    ? latest(minDate, dateFrom ?? undefined)
    : permissionMinTo;

  return (
    <Stack spacing={1} direction="row" sx={{ mb: 2.5 }}>
      <FromField
        disabled={disabled}
        label={hours ? 'Date' : 'From'}
        minDate={minDateFrom}
        maxDate={maxDateFrom}
        required
      />
      {!hours && (
        <ToField
          disabled={disabled}
          minDate={minDateTo}
          maxDate={maxDate}
          required
        />
      )}
    </Stack>
  );
}
